import { supabase } from './supabaseClient';
import type { Sprint } from '../types';

export const sprintService = {
  /**
   * Buscar todos os sprints de um projeto
   */
  async getByProject(projectId: string): Promise<Sprint[]> {
    const { data, error } = await supabase
      .from('sprints')
      .select('*')
      .eq('project_id', projectId)
      .order('position', { ascending: true });

    if (error) {
      if (error.message?.includes('AbortError')) return [];
      throw error;
    }
    return data as Sprint[];
  },

  /**
   * Criar novo sprint
   */
  async create(sprint: Partial<Sprint> & { project_id: string; name: string }): Promise<Sprint> {
    const { data, error } = await supabase
      .from('sprints')
      .insert([{
        project_id: sprint.project_id,
        name: sprint.name,
        goal: sprint.goal || '',
        start_date: sprint.start_date || null,
        end_date: sprint.end_date || null,
        status: sprint.status || 'planning',
        position: sprint.position ?? 0
      }])
      .select()
      .single();

    if (error) throw error;
    return data as Sprint;
  },

  /**
   * Atualizar sprint
   */
  async update(id: string, updates: Partial<Sprint>): Promise<Sprint> {
    const validFields = ['name', 'goal', 'start_date', 'end_date', 'status', 'position'];
    const updateData: Record<string, unknown> = { updated_at: new Date().toISOString() };

    for (const key of Object.keys(updates)) {
      if (validFields.includes(key)) {
        updateData[key] = (updates as Record<string, unknown>)[key];
      }
    }

    const { data, error } = await supabase
      .from('sprints')
      .update(updateData)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data as Sprint;
  },

  /**
   * Deletar sprint
   */
  async delete(id: string): Promise<void> {
    const { error } = await supabase
      .from('sprints')
      .delete()
      .eq('id', id);

    if (error) throw error;
  },

  /**
   * Iniciar sprint (enforça limite de 1 ativo por projeto)
   */
  async startSprint(id: string, projectId: string): Promise<Sprint> {
    // Completar qualquer sprint ativo do projeto
    await supabase
      .from('sprints')
      .update({ status: 'completed', updated_at: new Date().toISOString() })
      .eq('project_id', projectId)
      .eq('status', 'active');

    // Ativar o sprint selecionado
    const { data, error } = await supabase
      .from('sprints')
      .update({ status: 'active', updated_at: new Date().toISOString() })
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data as Sprint;
  },

  /**
   * Concluir sprint
   */
  async completeSprint(id: string): Promise<Sprint> {
    const { data, error } = await supabase
      .from('sprints')
      .update({ status: 'completed', updated_at: new Date().toISOString() })
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data as Sprint;
  }
};
